// Impresión del listado de anotados a un evento (la lista de puerta).
//
// El HTML se arma ACÁ con los datos que ya están en pantalla y no se pide al backend: el
// endpoint de admisiones devuelve la lista entera (no pagina), así que el papel sale de la
// misma fuente que la grilla y no puede discrepar con lo que el operador está mirando.

// Columnas del listado impreso, en el orden en que salen en la hoja. Es la ÚNICA lista: el
// checklist del modal se arma desde acá, así que agregar una columna no puede dejar el
// selector y el papel desfasados.
// `check` es la casilla vacía para tildar a mano en la puerta: sale como columna (angosta y
// sin encabezado) porque en papel el tilde es la mitad del trabajo del control de ingreso.
export const ADMISSION_PRINT_COLUMNS = [
    { key: 'check', label: 'Casilla para tildar', header: '' },
    { key: 'name', label: 'Nombre', header: 'Nombre' },
    // La mesa que el sorteo (o el mostrador) le asignó en una jornada del club; '-' sin mesa.
    { key: 'table', label: 'Mesa (jornadas del club)', header: 'Mesa' },
    { key: 'email', label: 'Mail', header: 'Mail' },
    { key: 'phone', label: 'Celular', header: 'Celular' },
    { key: 'ticketType', label: 'Tipo de entrada', header: 'Tipo de entrada' },
    { key: 'date', label: 'Fecha', header: 'Fecha' },
    { key: 'payment', label: 'Pago', header: 'Pago' },
    { key: 'status', label: 'Estado', header: 'Estado' },
];

export const ALL_PRINT_COLUMN_KEYS = ADMISSION_PRINT_COLUMNS.map((c) => c.key);

const HTML_ESCAPES = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};

// Los nombres/mails son texto libre tipeado en el mostrador o traído de la cuenta del
// cliente. Se escapa con una FUNCIÓN de reemplazo: con un string, un '$&' en el nombre
// se expandiría como metacarácter de replace y saldría otra cosa en el papel.
export function escapeHtml(value) {
    if (value == null) return '';
    return String(value).replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch]);
}

function printedAtLabel(now) {
    const d = now || new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Arma el documento imprimible. `rows` son filas YA formateadas por el componente (los
 * mismos textos que muestra la grilla), para que el papel diga exactamente lo de pantalla.
 * `columns` son las keys tildadas en el modal; el orden de la hoja no depende de ellas.
 *
 * Separado de la impresión en sí para poder testearlo sin tocar el DOM ni el diálogo.
 */
export function buildAdmissionsPrintHtml({
    eventTitle = '',
    eventDate = '',
    statusLabel = '',
    rows = [],
    columns = ALL_PRINT_COLUMN_KEYS,
    now = null,
} = {}) {
    // Se recorre ADMISSION_PRINT_COLUMNS y no `columns`: así el orden de la hoja es siempre el
    // mismo, sin importar en qué orden se hayan tildado las casillas.
    const elegidas = ADMISSION_PRINT_COLUMNS.filter((c) => columns.indexOf(c.key) !== -1);
    // Si no quedó ninguna (el modal lo impide, pero esto es una llamada más) se imprime todo:
    // una hoja con el encabezado y ninguna columna no le sirve a nadie.
    const cols = elegidas.length > 0 ? elegidas : ADMISSION_PRINT_COLUMNS;

    const subtitulo = [
        eventDate ? `Fecha: ${escapeHtml(eventDate)}` : '',
        `Filtro: ${escapeHtml(statusLabel || 'Todos')}`,
        `Total: ${rows.length}`,
        `Impreso: ${escapeHtml(printedAtLabel(now))}`,
    ].filter(Boolean).join(' &nbsp;·&nbsp; ');

    const encabezados = cols
        .map((c) => `<th${c.key === 'check' ? ' class="tilde"' : ''}>${escapeHtml(c.header)}</th>`)
        .join('');

    const celdas = (row) => cols
        .map((c) => (c.key === 'check'
            ? '<td class="tilde"></td>'
            : `<td>${escapeHtml(row[c.key])}</td>`))
        .join('');

    const cuerpo = rows.length === 0
        ? `<tr><td colspan="${cols.length}" class="vacio">No hay asistentes para mostrar.</td></tr>`
        : rows.map((r) => `<tr>${celdas(r)}</tr>`).join('');

    return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>Anotados — ${escapeHtml(eventTitle)}</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #000; margin: 0; }
  h1 { font-size: 14pt; margin: 0 0 2mm; }
  .sub { font-size: 9pt; color: #333; margin: 0 0 4mm; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #999; padding: 1.5mm 2mm; text-align: left; vertical-align: top; }
  th { background: #eee; font-size: 9pt; }
  /* El encabezado se repite en cada hoja: un listado largo se corta en varias páginas y
     sin esto la segunda queda sin saber qué columna es cuál. */
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  .tilde { width: 8mm; }
  .vacio { text-align: center; color: #666; }
</style>
</head>
<body>
  <h1>Anotados — ${escapeHtml(eventTitle)}</h1>
  <p class="sub">${subtitulo}</p>
  <table>
    <thead>
      <tr>${encabezados}</tr>
    </thead>
    <tbody>${cuerpo}</tbody>
  </table>
</body>
</html>`;
}

/**
 * Imprime el listado. Mismo camino que la comanda de cafetería (printComanda): iframe
 * SANDBOXED en vez de window.open, que el bloqueador de popups puede tragarse sin aviso.
 * Sin `allow-scripts`: el documento es sólo texto, y si el escape fallara un payload no
 * podría ejecutar nada en el origen del backoffice (donde vive el token).
 * `allow-same-origin` es lo que deja al padre disparar print(); `allow-modals`, el diálogo.
 */
export function printAdmissionsList(args) {
    const html = buildAdmissionsPrintHtml(args);
    const frame = document.createElement('iframe');
    frame.setAttribute('sandbox', 'allow-same-origin allow-modals');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
    frame.onload = () => {
        try {
            frame.contentWindow.focus();
            frame.contentWindow.print();
        } catch (e) {
            // noop: si el navegador rechaza el print, el iframe se limpia igual
        }
        setTimeout(() => {
            try { document.body.removeChild(frame); } catch (e) { /* noop */ }
        }, 1000);
    };
    frame.srcdoc = html;
    document.body.appendChild(frame);
    return html;
}
